/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PRODUCT_TYPE } from 'configs/constants';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import { getSubscriptionDetails } from 'services/api/subscriptionApi';
import {
  SUBSCRIPTION_CATEGORY,
  TDefaultPaymentMethod,
  TPaymentMethodDetails,
  TSubScriptionState,
  TSubscriptionType
} from 'typings';

// Tracks whether a GET /subscriptions is in flight, in module scope rather than
// Redux state so it is never persisted (a persisted `isLoading: true` from a
// webview closed mid-fetch would otherwise wrongly suppress the next fetch). Read
// only by the opt-in de-dupe below; written by every call.
let isSubscriptionFetchInFlight = false;

export const fetchAllSubscription = createAsyncThunk(
  'spaces/fetchAllSubscription',
  async (payload: { dedupe?: boolean } | undefined, thunkAPI) => {
    isSubscriptionFetchInFlight = true;
    try {
      const response = await getSubscriptionDetails();
      return response.data;
    } catch (error) {
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchAllSubscription',
          error_type: 'api_error'
        },
        extra: {
          payload
        }
      });
      return thunkAPI.rejectWithValue('failed');
    } finally {
      isSubscriptionFetchInFlight = false;
    }
  },
  {
    // Opt-in de-dupe. A caller that passes `{ dedupe: true }` skips its request
    // when one is already in flight — e.g. AppBridge pre-warms subscriptions, then
    // HomeBuySubscriptionV1 fetches on mount while that call is still pending, so
    // the duplicate collapses to nothing. Callers that pass no arg (including every
    // `.unwrap()` caller that reads the result, and all post-action refreshes) are
    // unaffected: their condition returns true and always runs.
    condition: (arg) => !(arg?.dedupe && isSubscriptionFetchInFlight)
  }
);

// A subscription's current payment method, flattened so the listing screens can
// match it against the saved-methods list. Cash App subscriptions expose their
// details under `payment_method_details`; cards still come through the legacy
// flat `card_details`.
const subscriptionDefaultPaymentMethod = (
  subscription?: TSubscriptionType
): TDefaultPaymentMethod | null => {
  if (!subscription) return null;
  const details = subscription.payment_method_details;
  if (details?.cashapp_details) {
    return {
      id: details.id,
      type: details.type,
      cashapp_details: details.cashapp_details
    };
  }
  return subscription.card_details ?? null;
};

const initialSubscriptionState: TSubScriptionState = {
  subscriptionData: [],
  homeSecuritySubscriptionList: [],
  birdSubscriptionList: [],
  petSubscriptionList: [],
  trailSubscriptionList: [],
  babySubscriptionList: [],
  dataSubscriptionList: [],
  isLoading: false,
  defaultPaymentMethod: null,
  birdDefaultPaymentMethod: null,
  petDefaultPaymentMethod: null,
  trailDefaultPaymentMethod: null,
  babyDefaultPaymentMethod: null,
  selectedSubscriptionInfo: null,
  selectedSKUInfo: null
};

export const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState: initialSubscriptionState,
  reducers: {
    clearSubscriptionData: (state) => {
      state.subscriptionData = [];
      state.birdSubscriptionList = [];
      state.petSubscriptionList = [];
      state.trailSubscriptionList = [];
      state.babySubscriptionList = [];
      state.homeSecuritySubscriptionList = [];
      state.dataSubscriptionList = [];
      state.birdDefaultPaymentMethod = null;
      state.petDefaultPaymentMethod = null;
      state.trailDefaultPaymentMethod = null;
      state.babyDefaultPaymentMethod = null;
      state.defaultPaymentMethod = null;
      state.selectedSubscriptionInfo = null;
      state.selectedSKUInfo = null;
    },
    updateSubscriptionData: (state, action) => {
      state.subscriptionData = action.payload;
    },
    setSelectedSubscriptionInfo: (state, action) => {
      state.selectedSubscriptionInfo = action.payload;
    },
    setSelectedSKUInfo: (state, action) => {
      state.selectedSKUInfo = action.payload;
    },
    clearSelectedSubscriptionInfo: (state) => {
      state.selectedSubscriptionInfo = null;
    },
    setDefaultPaymentMethod: (state, action) => {
      state.defaultPaymentMethod = action.payload as TPaymentMethodDetails;
    },
    setBirdDefaultPaymentMethod: (state, action) => {
      state.birdDefaultPaymentMethod = action.payload as TPaymentMethodDetails;
    },
    setPetDefaultPaymentMethod: (state, action) => {
      state.petDefaultPaymentMethod = action.payload as TPaymentMethodDetails;
    },
    setTrailDefaultPaymentMethod: (state, action) => {
      state.trailDefaultPaymentMethod = action.payload as TPaymentMethodDetails;
    },
    setBabyDefaultPaymentMethod: (state, action) => {
      state.babyDefaultPaymentMethod = action.payload as TPaymentMethodDetails;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptionData = action.payload as TSubscriptionType[];
        // Product lists exclude Data subscriptions: data is space-scoped and
        // bucketed separately in dataSubscriptionList, even if a legacy data
        // doc still carries a product_id.
        const isDataSub = (subscription: TSubscriptionType) =>
          subscription.sku_category === SUBSCRIPTION_CATEGORY.DATA;
        state.dataSubscriptionList = action.payload.filter(isDataSub);
        state.homeSecuritySubscriptionList = action.payload.filter(
          (subscription) =>
            subscription.product_id === PRODUCT_TYPE.HOME_SECURITY &&
            !isDataSub(subscription)
        );
        state.defaultPaymentMethod = subscriptionDefaultPaymentMethod(
          state.homeSecuritySubscriptionList[0]
        );
        state.birdSubscriptionList = action.payload.filter(
          (subscription) => subscription.product_id === PRODUCT_TYPE.BIRD
        );
        state.birdDefaultPaymentMethod = subscriptionDefaultPaymentMethod(
          state.birdSubscriptionList[0]
        );
        state.petSubscriptionList = action.payload.filter(
          (subscription) => subscription.product_id === PRODUCT_TYPE.PET
        );
        state.petDefaultPaymentMethod = subscriptionDefaultPaymentMethod(
          state.petSubscriptionList[0]
        );
        state.babySubscriptionList = action.payload.filter(
          (subscription) => subscription.product_id === PRODUCT_TYPE.BABY
        );
        state.babyDefaultPaymentMethod = subscriptionDefaultPaymentMethod(
          state.babySubscriptionList[0]
        );
        state.trailSubscriptionList = action.payload.filter(
          (subscription) =>
            subscription.product_id === PRODUCT_TYPE.TRAIL &&
            !isDataSub(subscription)
        );
        state.trailDefaultPaymentMethod = subscriptionDefaultPaymentMethod(
          state.trailSubscriptionList[0]
        );
      })
      .addCase(fetchAllSubscription.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllSubscription.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

// Action creators are generated for each case reducer function
export const {
  clearSubscriptionData,
  updateSubscriptionData,
  setSelectedSubscriptionInfo,
  clearSelectedSubscriptionInfo,
  setDefaultPaymentMethod,
  setBirdDefaultPaymentMethod,
  setPetDefaultPaymentMethod,
  setBabyDefaultPaymentMethod,
  setSelectedSKUInfo
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
